import * as sentry from './sentry'
import { Loca_extractAddress } from '../../zap/Loca/Loca.extractAddress'
import { Plat_isCordova } from '../../zap/Plat/Plat.device'
import { Util_calcDistance, Util_compareObjDistances } from '../../zap/Util/Util.distance'
import { Util_doNothing } from '../../zap/Util/Util.doNothing'
import type {
    Type_IFocusListLocation,
    Type_ILocation,
    Type_ILocationFocusList,
    Type_IProcessedLocation,
    Type_TProcessedFocusListLocation,
} from '../../zap/Type/Type.hive'

export const toLocation = (locations: Type_IProcessedLocation[]) => {
    const locationMap = new Map<number, Type_IProcessedLocation>(
        locations.map((location): [number, Type_IProcessedLocation] => [location.id, location])
    )

    return (entry: Type_IFocusListLocation): Type_TProcessedFocusListLocation | undefined => {
        // returns the combined data (from the location and focusList location)
        // or undeffined if the location is not owned by the user

        const local = locationMap.get(entry.id)

        return local ? { ...local, ...entry } : undefined
    }
}

export const sortByWorthThenName = (
    a: Type_TProcessedFocusListLocation,
    b: Type_TProcessedFocusListLocation
): number => {
    if (a.worth && b.worth) {
        const diff = b.worth - a.worth
        if (diff !== 0) return diff
    }
    return sortByName(a, b)
}

export const sortByName = (a: Type_ILocation, b: Type_ILocation): number => {
    return a.name.localeCompare(b.name)
}

export function matchesSearch(searchText: string, location: Type_IProcessedLocation): boolean {
    if (searchText.length === 0) return true

    return (
        location.name.toLowerCase().includes(searchText.toLowerCase()) ||
        location.address.toLowerCase().includes(searchText.toLowerCase())
    )
}

export const processLocation =
    (lat: number, lon: number) =>
    (location: Type_ILocation): Type_IProcessedLocation => {
        return {
            ...location,
            address: Loca_extractAddress(location),
            mapAddress:
                location.xCoord === 0 && location.yCoord === 0
                    ? location.name + ',' + location.address1 + ',' + location.city
                    : location.xCoord + ',' + location.yCoord,
            distance: lat && lon ? Util_calcDistance(lat, lon, location.xCoord, location.yCoord, 2) : undefined,
            visible: true,
        }
    }

export function processLocations(inputLocations: Type_ILocation[], lat: number, lon: number) {
    const locations = inputLocations.map(processLocation(lat, lon))

    // Sort by distance if we have distances
    if (lat && lon) {
        locations.sort(Util_compareObjDistances)
    }

    return locations
}

export function completedLocations(
    locations: Type_IProcessedLocation[],
    focusList: Type_ILocationFocusList
): Type_TProcessedFocusListLocation[] {
    // returns the combined data (from the location and focusList location)
    // where the location is owned by the user (locations) and completed
    return focusList.locations
        .map(toLocation(locations))
        .filter((entry) => entry !== undefined)
        .filter((entry) => entry.reports.length >= entry.callQuantity)
}

export function locationsToComplete(
    locations: Type_IProcessedLocation[],
    focusList: Type_ILocationFocusList
): Type_TProcessedFocusListLocation[] {
    // returns the combined data (from the location and focusList location)
    // where the location is owned by the user (locations) and incomplete
    return focusList.locations
        .map(toLocation(locations))
        .filter((entry) => entry !== undefined)
        .filter((entry) => entry.reports.length < entry.callQuantity)
}

export function ownedLocations(
    locations: Type_IProcessedLocation[],
    focusList: Type_ILocationFocusList
): Type_TProcessedFocusListLocation[] {
    // returns the combined data (from the location and focusList location)
    // where the location is owned by the user (locations)
    return focusList.locations.map(toLocation(locations)).filter((entry) => entry !== undefined)
}

export function findAddressInGoogleMaps(address: string) {
    if (Plat_isCordova()) {
        launchnavigator.navigate(address, null, Util_doNothing, (error: any) => sentry.err(error, {}))
    } else {
        window.open('http://maps.google.com/maps?z=12&t=m&q=loc:' + address, '_BLANK')
    }
}
